import { useState, useRef } from "react";
import { useAppContext } from "@/App";
import api from "@/lib/api";
import {
  Upload, FileText, Image, Table2, Loader2, Check, AlertTriangle,
  ChevronDown, ChevronUp, MapPin, Wrench, Users, Fuel, X
} from "lucide-react";

export default function DataImport({ hubId, onImportComplete }) {
  const { auth } = useAppContext();
  const fileRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [expanded, setExpanded] = useState({ sites: true, machines: false, personnel: false, fuel: false });
  const [error, setError] = useState("");

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "csv", "jpg", "jpeg", "png", "webp", "txt"].includes(ext)) {
      setError("Supported formats: PDF, CSV, JPG, PNG, TXT");
      return;
    }

    setUploading(true);
    setError("");
    setImportResult(null);
    setConfirmed(false);

    try {
      const result = await api.uploadAndParse(auth.jwt, hubId, file);
      setImportResult(result);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleConfirmImport = async () => {
    if (!importResult?.id) return;
    setConfirming(true);
    try {
      const result = await api.confirmImport(auth.jwt, hubId, importResult.id);
      setConfirmed(true);
      if (onImportComplete) onImportComplete(result);
    } catch (err) {
      setError(err?.response?.data?.detail || "Import confirmation failed");
    }
    setConfirming(false);
  };

  const data = importResult?.extracted_data || {};
  const hasSites = (data.sites?.length || 0) > 0;
  const hasMachines = (data.machines?.length || 0) > 0;
  const hasPersonnel = (data.personnel?.length || 0) > 0;
  const hasFuel = (data.fuel_data?.length || 0) > 0;
  const hasData = hasSites || hasMachines || hasPersonnel || hasFuel;

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="space-y-4" data-testid="data-import-panel">
      {/* Upload Zone */}
      <div
        className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
          uploading ? "border-lime-400 bg-lime-500/5" : "border-slate-200 hover:border-lime-400 bg-slate-50/80"
        }`}
        onClick={() => !uploading && fileRef.current?.click()}
        data-testid="upload-zone"
      >
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.csv,.jpg,.jpeg,.png,.webp,.txt"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="file-input"
        />
        {uploading ? (
          <div className="py-4">
            <Loader2 className="w-10 h-10 text-lime-500 animate-spin mx-auto mb-3" />
            <p className="text-sm font-bold text-lime-600">Philogic AI analyzing document...</p>
            <p className="text-xs text-slate-400 mt-1">Extracting sites, machines, and personnel data</p>
          </div>
        ) : (
          <>
            <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-400 mb-1">Upload Document</p>
            <p className="text-xs text-slate-400">PDF, CSV, or Photo — AI will extract structured data</p>
            <div className="flex items-center justify-center gap-4 mt-4">
              <span className="flex items-center gap-1.5 text-[10px] text-slate-400 font-bold uppercase"><FileText className="w-3.5 h-3.5" /> PDF</span>
              <span className="flex items-center gap-1.5 text-[10px] text-slate-400 font-bold uppercase"><Table2 className="w-3.5 h-3.5" /> CSV</span>
              <span className="flex items-center gap-1.5 text-[10px] text-slate-400 font-bold uppercase"><Image className="w-3.5 h-3.5" /> Photo</span>
            </div>
          </>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl" data-testid="import-error">
          <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
          <p className="text-sm text-red-400 font-medium">{error}</p>
          <button onClick={() => setError("")} className="ml-auto"><X className="w-4 h-4 text-red-400" /></button>
        </div>
      )}

      {/* Results */}
      {importResult && hasData && !confirmed && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden" data-testid="import-results">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-slate-800">Philogic Extraction Results</p>
              <p className="text-xs text-slate-400 mt-0.5">{importResult.file_name}</p>
            </div>
            <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${importResult.status === "complete" ? "bg-lime-100 text-lime-600" : "bg-amber-100 text-amber-400"}`}>
              {importResult.status?.toUpperCase()}
            </span>
          </div>

          {/* Sites */}
          {hasSites && (
            <div className="border-b border-slate-200">
              <button onClick={() => toggle("sites")} className="w-full flex items-center justify-between p-3 hover:bg-slate-100/30" data-testid="toggle-sites">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-lime-600" />
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Sites ({data.sites.length})</span>
                </div>
                {expanded.sites ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              {expanded.sites && (
                <div className="px-3 pb-3 space-y-2 max-h-60 overflow-y-auto">
                  {data.sites.map((s, i) => (
                    <div key={s.name ? `${s.name}-${s.city}` : `site-${i}`} className="p-3 bg-white rounded border border-slate-200">
                      <p className="text-sm font-bold text-slate-800">{s.name}</p>
                      <p className="text-xs text-slate-400">{s.address}, {s.city}</p>
                      {s.code && <p className="text-[10px] text-slate-400 font-mono mt-1">{s.code}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Machines */}
          {hasMachines && (
            <div className="border-b border-slate-200">
              <button onClick={() => toggle("machines")} className="w-full flex items-center justify-between p-3 hover:bg-slate-100/30" data-testid="toggle-machines">
                <div className="flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-amber-400" />
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Machines ({data.machines.length})</span>
                </div>
                {expanded.machines ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              {expanded.machines && (
                <div className="px-3 pb-3 space-y-2 max-h-60 overflow-y-auto">
                  {data.machines.map((m, i) => (
                    <div key={m.machine_number || `machine-${i}`} className="p-2.5 bg-white rounded border border-slate-200 flex items-center gap-3">
                      <span className="text-xs font-mono text-lime-600 shrink-0">{m.machine_number}</span>
                      <span className="text-xs text-slate-400 truncate">{m.name}</span>
                      {m.brand && <span className="text-[10px] text-slate-400 ml-auto shrink-0">{m.brand}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Personnel */}
          {hasPersonnel && (
            <div className="border-b border-slate-200">
              <button onClick={() => toggle("personnel")} className="w-full flex items-center justify-between p-3 hover:bg-slate-100/30" data-testid="toggle-personnel">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-400" />
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Personnel ({data.personnel.length})</span>
                </div>
                {expanded.personnel ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              {expanded.personnel && (
                <div className="px-3 pb-3 space-y-2 max-h-60 overflow-y-auto">
                  {data.personnel.map((p, i) => (
                    <div key={`${p.name}-${p.role}-${i}`} className="p-2.5 bg-white rounded border border-slate-200 flex items-center gap-3">
                      <span className="text-xs font-bold text-slate-400">{p.name}</span>
                      <span className="text-[10px] text-slate-400 uppercase">{p.role}</span>
                      {p.phone && <span className="text-[10px] text-slate-400 ml-auto">{p.phone}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Fuel */}
          {hasFuel && (
            <div className="border-b border-slate-200">
              <button onClick={() => toggle("fuel")} className="w-full flex items-center justify-between p-3 hover:bg-slate-100/30" data-testid="toggle-fuel">
                <div className="flex items-center gap-2">
                  <Fuel className="w-4 h-4 text-red-400" />
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Fuel Data ({data.fuel_data.length})</span>
                </div>
                {expanded.fuel ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              {expanded.fuel && (
                <div className="px-3 pb-3 space-y-2 max-h-48 overflow-y-auto">
                  {data.fuel_data.map((f, i) => (
                    <div key={`${f.tank_type}-${i}`} className="p-2.5 bg-white rounded border border-slate-200 flex items-center gap-3">
                      <span className="text-xs text-slate-400">{f.tank_type}</span>
                      {f.capacity_liters > 0 && <span className="text-xs text-amber-400 ml-auto">{f.capacity_liters}L</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Confirm button */}
          <div className="p-4">
            <button
              data-testid="confirm-import-btn"
              onClick={handleConfirmImport}
              disabled={confirming}
              className="w-full py-3.5 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-100 text-slate-950 rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2"
            >
              {confirming ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-4 h-4" /> Confirm & Import to Hub</>}
            </button>
          </div>
        </div>
      )}

      {/* Confirmed */}
      {confirmed && (
        <div className="p-5 bg-lime-50 border border-lime-300 rounded-xl text-center" data-testid="import-confirmed">
          <Check className="w-8 h-8 text-lime-600 mx-auto mb-2" />
          <p className="text-sm font-bold text-lime-600">Data imported successfully</p>
          <p className="text-xs text-slate-400 mt-1">Sites, machines, and personnel have been added to the hub</p>
        </div>
      )}
    </div>
  );
}
